import { DOCUMENT, CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { UrlReferenceService } from '../../../shared/services/url-reference.service';
import { CompanyService } from '../../../shared/services/company.service';
import { AuthService } from '../../../shared/services/auth.service';
import { from } from 'rxjs';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-quota',
  imports: [CommonModule, TranslateModule, FormsModule],
  standalone: true,
  templateUrl: './quota.component.html',
  styleUrl: './quota.component.scss',
})
export class QuotaComponent implements OnInit, OnDestroy {
  direction: 'ltr' | 'rtl' = 'ltr';
  private observer?: MutationObserver;

  crNumber: string = '';
  policyStartDate: string = '';
  activeStep: number = 1;
  phoneNumber: string = '';
  email: string = '';
  entity700Number: string = '';
  fromGetQuote: string | null = null;
  fromSignUp: string | null = null;
  minDate: string = '';
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private urlRef: UrlReferenceService,
    private companyService: CompanyService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.updateDirection();

    // Watch for lang attribute changes on <html>
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'lang'
        ) {
          this.updateDirection();
        }
      }
    });

    this.observer.observe(this.document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
    });

    this.fromGetQuote = localStorage.getItem('from-get-quote');
    console.log('from-get-quote:', this.fromGetQuote);
    this.fromSignUp = localStorage.getItem('from-signup');
    console.log('from-signup:', this.fromSignUp);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.policyStartDate = tomorrow.toISOString().split('T')[0]; // default to tomorrow
    this.minDate = tomorrow.toISOString().split('T')[0]; // format YYYY-MM-DD
  }

  private updateDirection() {
    const lang = this.document.documentElement.lang || 'en';
    this.direction = ['ar', 'he', 'fa', 'ur'].includes(lang) ? 'rtl' : 'ltr';
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  validateInputs(): boolean {
    if (!/^\d{10}$/.test(this.crNumber)) {
      this.toastr.warning('CR Number must be exactly 10 digits!');
      return false;
    }

    if (!this.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
      this.toastr.warning('Please enter a valid email address!');
      return false;
    }

    if (!this.policyStartDate || this.policyStartDate < this.minDate) {
      this.toastr.warning('Policy start date must be a future date!');
      return false;
    }

    // if (this.phoneNumber && !!/^\d{10,15}$/.test(this.phoneNumber)) {
    //   this.toastr.warning('Please enter a valid phone number!');
    // }

    return true;
  }

  onContinue() {
    if (!this.validateInputs()) {
      return;
    }
    if (!this.crNumber) {
      this.toastr.warning('Please enter a CR number');
      return;
    }

    if (this.policyStartDate) {
      localStorage.setItem('savedPolicyStartDate', this.policyStartDate);
      console.log(
        'Policy start date saved from landing page:',
        this.policyStartDate
      );
    }

    let companyData = {
      crNumber: this.crNumber,
      policyStartDate: this.policyStartDate,
      phoneNumber: this.phoneNumber,
      email: this.email,
      entity700Number: this.entity700Number,
    };

    this.companyService.setCompanyData(companyData);
    this.authService.setUserData({
      email: this.email,
      phoneNumber: this.phoneNumber,
      isSignup: false,
    });

    this.companyService.verifyCrNumber(this.crNumber, '').subscribe({
      next: (response: any) => {
        console.log('CR Verified & Company Saved from landing page:', response);
        this.companyService.setCompanyDataResponse(response);

        const mergedCompanyData = {
          ...response,
          crNumber: this.crNumber,
          policyStartDate: this.policyStartDate,
          email: this.email,
          phoneNumber: this.phoneNumber,
          entity700Number: this.entity700Number,
        };

        console.log('Merged company data:', mergedCompanyData);
        this.companyService.setCompanyData(mergedCompanyData);
        this.companyService.setCompanyDetails(mergedCompanyData);

        localStorage.setItem('company', JSON.stringify(mergedCompanyData));
        console.log('Company data saved to localStorage for onboarding');

        localStorage.setItem('from-get-quote', 'true');

        const userData = this.authService.getUserData();
        console.log('User Data from landing:', userData);

        const otpPayload = {
          otpSentVia: userData.phoneNumber ? 'SMS' : 'EMAIL',
          email: userData.email,
          phoneNumber: userData.phoneNumber,
        };

        this.authService.generateOtp(otpPayload).subscribe({
          next: () => this.router.navigate(['/verify-otp']),
          error: (err) => {
            console.error('OTP generation failed', err);
            this.handleOtpError(err);
          },
        });
      },
      error: (err) => {
        console.error('Error verifying CR:', err);
        this.toastr.error('Could not verify CR. Please try again.', 'Error');
      },
    });
  }

  private handleOtpError(error: any): void {
    let errorMessage = 'Failed to generate OTP. Please try again.';
    let errorCode = '';

    console.log('Full error object:', error);

    try {
      let errorData: any;

      if (typeof error.error === 'string') {
        errorData = JSON.parse(error.error);
      } else if (typeof error.error === 'object') {
        errorData = error.error;
      }

      if (errorData?.message) {
        errorMessage = errorData.message;
        errorCode = errorData.errorCode || '';
      }
    } catch (parseError) {
      console.error('Error parsing error response:', parseError);

      switch (error.status) {
        case 409:
          errorMessage =
            'This email or phone number is already registered. Please try logging in instead.';
          break;
        case 400:
          errorMessage = 'Invalid request. Please check your information.';
          break;
        case 429:
          errorMessage = 'Too many requests. Please wait before trying again.';
          break;
        case 502:
          errorMessage =
            'Service temporarily unavailable. Please try again later.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        default:
          errorMessage = `Error ${error.status}: Please try again.`;
      }
    }

    // Handle specific error codes for better UX
    if (errorCode === 'USER_EXISTS') {
      this.toastr.warning(errorMessage, 'User Already Exists', {
        timeOut: 7000,
        closeButton: true,
      });

      // Optionally redirect to login or show login option
      // this.showLoginOption = true;
    } else {
      this.toastr.error(errorMessage, 'OTP Generation Failed', {
        timeOut: 5000,
        closeButton: true,
      });
    }
  }
}
